/**
 * StruxaFreight - live NZ freight estimate for the "Shipping this to a
 * customer?" panel (cart drawer + full cart page).
 *
 * Struxa's 4 domestic rates all live in a single Shopify shipping zone
 * ("Domestic - New Zealand") with no postcode-based conditions, so
 * Shopify's own /cart/prepare_shipping_rates.json + async_shipping_rates.json
 * endpoints always return all 4 rates for any NZ address - they are not
 * geographically filtered by Shopify itself. This module fills that gap:
 * it fetches the live rates (so prices always match whatever is configured
 * in Shopify Admin) and picks the one that matches the address using a
 * client-side urban/rural + island classification, so the trade customer
 * can see the correct freight cost before they check out.
 *
 * Rate names (Settings > Shipping and delivery > General profile > Domestic):
 *   "North Island Urban Shipping and Handling"  $20
 *   "North Island Rural Shipping and Handling"  $28
 *   "South Island Urban Shipping and Handling"  $25
 *   "South Island Rural Shipping and Handling"  $34
 *
 * Classification heuristics:
 *   - Rural: address line or city contains an RD (Rural Delivery) number,
 *     e.g. "123 RD2" / "RD 4" - the standard NZ Post/courier convention.
 *   - Island: NZ postcodes 7000-9999 are South Island, everything else
 *     (0000-6999) is North Island.
 * Neither heuristic is 100% - this is a best-effort estimate shown to the
 * trade customer, with a note that the exact rate is confirmed at checkout.
 */
(function (window) {
  'use strict';

  var rateCache = {};

  function classify(address1, city, zip) {
    var ruralPattern = /\bRD\s?-?\d/i;
    var isRural = ruralPattern.test(address1 || '') || ruralPattern.test(city || '');
    var zipNum = parseInt(String(zip || '').replace(/\D/g, ''), 10);
    var isSouth = !isNaN(zipNum) && zipNum >= 7000 && zipNum <= 9999;
    var island = isSouth ? 'South Island' : 'North Island';
    var type = isRural ? 'Rural' : 'Urban';
    return { island: island, type: type, isRural: isRural, isSouth: isSouth, label: island + ' ' + type };
  }

  function toQueryString(addr) {
    var params = new URLSearchParams();
    Object.keys(addr).forEach(function (k) {
      if (addr[k]) params.set('shipping_address[' + k + ']', addr[k]);
    });
    return params.toString();
  }

  function pollRates(addr, attempt) {
    attempt = attempt || 0;
    if (attempt > 10) return Promise.reject(new Error('struxa_freight_timeout'));
    return fetch('/cart/async_shipping_rates.json?' + toQueryString(addr), {
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (res.status === 202) {
        return new Promise(function (resolve) {
          setTimeout(function () {
            resolve(pollRates(addr, attempt + 1));
          }, 550);
        });
      }
      if (!res.ok) throw new Error('struxa_freight_failed');
      return res.json();
    });
  }

  function fetchRates(addr) {
    return fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ shipping_address: addr }),
    }).then(function () {
      return pollRates(addr, 0);
    });
  }

  function normalizePrice(rate) {
    var raw = rate.price;
    var n = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (isNaN(n)) return null;
    // Defensive: Shopify's cart-rate endpoints usually return decimal dollars
    // (e.g. "20.00"), but guard against a cents integer just in case.
    if (Number.isInteger(n) && n >= 500) n = n / 100;
    return n;
  }

  window.StruxaFreight = {
    classify: classify,

    getRatesForAddress: function (addr) {
      var key = JSON.stringify(addr);
      if (rateCache[key]) return rateCache[key];
      var p = fetchRates(addr).then(function (data) {
        return (data && data.shipping_rates) || [];
      });
      rateCache[key] = p;
      p.catch(function () {
        delete rateCache[key];
      });
      return p;
    },

    findMatchingRate: function (rates, addr) {
      var c = classify(addr.address1, addr.city, addr.zip);
      var match = (rates || []).filter(function (r) {
        return r.name && r.name.indexOf(c.island) !== -1 && r.name.indexOf(c.type) !== -1;
      })[0];
      return { rate: match || null, classification: c, price: match ? normalizePrice(match) : null };
    },
  };
})(window);
